import { useCallback, useEffect, useMemo, useState } from 'react';
import { AuthGate } from './components/AuthGate';
import { Layout, type ViewName } from './components/Layout';
import { ChatPanel } from './features/chat/ChatPanel';
import { DashboardView } from './features/dashboard/DashboardView';
import { ReviewView } from './features/review/ReviewView';
import { BrowseView } from './features/browse/BrowseView';
import { SummariesView } from './features/summaries/SummariesView';
import { GamesView } from './features/games/GamesView';
import { AnalyticsView } from './features/analytics/AnalyticsView';
import { SettingsView } from './features/settings/SettingsView';
import { useDecks } from './hooks/useDecks';
import * as storage from './services/storage/storage';
import type { Card, DeckId } from './types';

const VIEWS = new Set<ViewName>(['dashboard', 'review', 'browse', 'summaries', 'games', 'analytics', 'settings']);

export function App() {
  const { cards, loading, error, reload } = useDecks();
  const [view, setView] = useState<ViewName>(() => viewFromHash() ?? 'dashboard');
  const [activeDeckId, setActiveDeckId] = useState<DeckId>('bs');
  const [activeCard, setActiveCard] = useState<{ deckId: DeckId; card: Card } | null>(null);
  const [version, setVersion] = useState(0);
  const data = useMemo(() => storage.load(), [version]);

  useEffect(() => {
    const onHash = () => {
      const next = viewFromHash();
      if (next) setView(next);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = data.settings.theme;
    document.body.classList.toggle('dark', data.settings.theme === 'dark');
  }, [data.settings.theme, version]);

  const refreshData = useCallback(() => setVersion((value) => value + 1), []);

  // Neu rendern, wenn der aktive User wechselt oder nach dem Login die
  // Server-Daten in den Storage übernommen wurden.
  useEffect(() => {
    window.addEventListener('storage:user-changed', refreshData);
    window.addEventListener('storage:replaced', refreshData);
    return () => {
      window.removeEventListener('storage:user-changed', refreshData);
      window.removeEventListener('storage:replaced', refreshData);
    };
  }, [refreshData]);

  function navigate(next: ViewName) {
    setView(next);
    const hash = `#/${next}`;
    if (location.hash !== hash) history.pushState(null, '', hash);
  }

  function startReview(deckId: DeckId) {
    storage.getDeckState(deckId);
    setActiveDeckId(deckId);
    refreshData();
    navigate('review');
  }

  function toggleTheme() {
    const loaded = storage.load();
    loaded.settings.theme = loaded.settings.theme === 'dark' ? 'light' : 'dark';
    storage.save();
    refreshData();
  }

  return (
    <AuthGate>
      {({ user, syncStatus, onLogout }) => (
        <Layout activeView={view} onNavigate={navigate} streak={data.streak.count} user={user} syncStatus={syncStatus} onLogout={onLogout} onToggleTheme={toggleTheme}>
          {view === 'dashboard' && <DashboardView cards={cards} data={data} loading={loading} error={error} onReload={() => void reload(true)} onStartReview={startReview} />}
          {view === 'review' && <ReviewView activeDeckId={activeDeckId} cards={cards} data={data} onDeckChange={(deckId) => { storage.getDeckState(deckId); setActiveDeckId(deckId); refreshData(); }} onDataChanged={refreshData} onActiveCardChange={(deckId, card) => setActiveCard(card ? { deckId, card } : null)} />}
          {view === 'browse' && <BrowseView cards={cards} data={data} onSelectCard={(deckId, card) => setActiveCard({ deckId, card })} onStartReview={startReview} onDataChanged={refreshData} />}
          {view === 'summaries' && <SummariesView />}
          {view === 'games' && <GamesView />}
          {view === 'analytics' && <AnalyticsView cards={cards} data={data} />}
          {view === 'settings' && <SettingsView data={data} cards={cards} onDataChanged={refreshData} />}
          <ChatPanel active={activeCard} />
        </Layout>
      )}
    </AuthGate>
  );
}

function viewFromHash(): ViewName | null {
  const hash = location.hash || '';
  // Only treat hashes of the form "#/view" as view navigation.
  // Plain fragment links like "#abschnitt-1" are intra-page anchors (e.g. summary TOC)
  // and must not change the active view.
  if (!hash.startsWith('#/')) return null;
  const candidate = hash.replace(/^#\/?/, '').split('/')[0] as ViewName;
  return VIEWS.has(candidate) ? candidate : null;
}
